import React, { useState, useEffect, useRef } from "react"

import * as Yup from "yup"
import { Formik, Form, Field } from "formik"

import TextField from "@material-ui/core/TextField"
import DialogActions from "@material-ui/core/DialogActions"
import Typography from "@material-ui/core/Typography"

import CircularProgress from "@material-ui/core/CircularProgress"

import { toast } from "react-toastify"

import api from "../../services/api"
import toastError from "../../errors/toastError"

import DefaultButton from "../DefaultButton"
import Modal from "../Modal"

import useStyles from "./ContactModalStyles"
import { Grid } from "@mui/material"
import useContactsStore from "../../stores/ContactsStore"
const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .transform((value) => value && value.replace(/\s/g, ""))
    .min(2, "Muito pequeno!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  number: Yup.string()
    .trim()
    .transform((value) => value && value.replace(/\s/g, ""))
    .min(8, "Muito curto!")
    .max(50, "Muito longo!"),
  email: Yup.string()
    .trim()
    .transform((value) => value && value.replace(/\s/g, ""))
    .email("Email inválido")
    .max(100, "Muito longo!"),
  empresa: Yup.string()
    .nullable()
    .trim()
    .transform((value) => value && value.replace(/\s/g, ""))
    .max(60, "Muito longo!"),
})

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
  const classes = useStyles()
  const isMounted = useRef(true)

  const initialState = {
    name: "",
    number: "",
    email: "",
    empresa: "",
  }

  const sanitizeContact = (c) => ({
    name: c && c.name ? String(c.name) : "",
    number: c && (c.number || c.number === 0) ? String(c.number).trim() : "",
    email: c && c.email ? String(c.email) : "",
    empresa: c && c.empresa ? String(c.empresa) : "",
  })

  const [contact, setContact] = useState(initialState)
  const [isClosing, setIsClosing] = useState(false)
  const [isVisible, setIsVisible] = useState(false) // Change initial state to false
  const formikRef = useRef()
  const { updateContact } = useContactsStore()

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    const fetchContact = async () => {
      if (!open) return

      if (initialValues) {
        setContact((prevState) => {
          const cNumber = (initialValues?.number && String(initialValues.number).trim()) || ""
          return { ...prevState, ...sanitizeContact({ ...initialValues, number: cNumber }) }
        })
      }

      if (!contactId) return

      try {
        const { data } = await api.get(`/contacts/${contactId}`)
        if (isMounted.current) {
          const cNumber = (data?.number && String(data.number).trim()) || ""
          setContact(sanitizeContact({ ...data, number: cNumber }))
        }
      } catch (err) {
        toastError(err)
      }
    }

    fetchContact()
  }, [contactId, open, initialValues])

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      setIsVisible(false)
      setIsClosing(false)
      onClose()
    }, 500)
    setContact(initialState)
  }

  const handleSaveContact = async (values) => {
    try {
      if (contactId) {
        const { data: oContatoRecord } = await api.put(`/contacts/${contactId}`, values)
        updateContact(oContatoRecord)
        if (onSave) onSave(oContatoRecord)
      } else {
        const { data: oContatoRecord } = await api.post("/contacts", values)
        if (onSave) onSave(oContatoRecord)
        updateContact(oContatoRecord)
      }
      toast.success("Contato salvo com sucesso.")
      handleClose()
    } catch (err) {
      toastError(err)
    }
  }

  const RenderFooter = () => {
    return (
      <DialogActions className={classes.buttonsContainer}>
        <DefaultButton
          stylization="primary"
          disabled={formikRef.current?.isSubmitting}
          className={classes.btnFullWidth}
          onClick={() => formikRef.current?.submitForm()}
        >
          Gravar
          {formikRef.current?.isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
        </DefaultButton>
      </DialogActions>
    )
  }

  return (
    <div>
      <Modal
        title={contactId ? `Editar contato` : `Novo contato`}
        modalOpen={open}
        onClose={onClose}
        footer={RenderFooter()}
      >
        <Formik
          innerRef={formikRef}
          initialValues={contact}
          enableReinitialize={true}
          validationSchema={ContactSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveContact(values)
              actions.setSubmitting(false)
            }, 400)
          }}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form className={classes.formContainer}>
              <Typography variant="subtitle1">Dados do contato</Typography>
              <Grid item xs={12} md={4}>
                <Field
                  as={TextField}
                  label="Nome"
                  name="name"
                  autoFocus
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  className={classes.input}
                  InputLabelProps={{
                    classes: {
                      root: classes.label, // Aplica o estilo da label
                    },
                  }}
                  InputProps={{
                    classes: { input: classes.textField },
                  }}
                />
              </Grid>
              <Grid xs={12} md={4} item>
                <Field
                  as={TextField}
                  label="Telefone"
                  name="number"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  error={touched.number && Boolean(errors.number)}
                  helperText={touched.number && errors.number}
                  placeholder="Número"
                  className={classes.input}
                  InputLabelProps={{
                    classes: {
                      root: classes.label, // Aplica o estilo da label
                    },
                  }}
                  InputProps={{
                    classes: { input: classes.textField },
                  }}
                />
              </Grid>
              <div>
                <Grid xs={12} md={4} item>
                  <Field
                    as={TextField}
                    label="Email"
                    name="email"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    placeholder="Email address"
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    className={classes.input}
                    InputLabelProps={{
                      classes: {
                        root: classes.label, // Aplica o estilo da label
                      },
                    }}
                    InputProps={{
                      classes: { input: classes.textField },
                    }}
                  />
                </Grid>
                <Grid xs={12} md={4} item>
                  <Field
                    as={TextField}
                    label="Empresa"
                    name="empresa"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                    error={touched.empresa && Boolean(errors.empresa)}
                    helperText={touched.empresa && errors.empresa}
                    className={classes.input}
                    InputLabelProps={{
                      classes: {
                        root: classes.label, // Aplica o estilo da label
                      },
                    }}
                    InputProps={{
                      classes: { input: classes.textField },
                      inputProps: {
                        maxLength: 60, // Limita o campo a 60 caracteres
                      },
                    }}
                  />
                </Grid>
              </div>
              {/*
              <Typography style={{ marginBottom: 8, marginTop: 12 }} variant="subtitle1">
                Informações adicionais
              </Typography>
              <FieldArray name="extraInfo">
                {({ push, remove }) => (
                  <>
                    {values.extraInfo &&
                      values.extraInfo.length > 0 &&
                      values.extraInfo.map((info, index) => (
                        <div className={classes.extraAttr} key={`${index}-info`}>
                          <Field
                            as={TextField}
                            label="Nome do campo"
                            name={`extraInfo[${index}].name`}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                          <Field
                            as={TextField}
                            label="Valor"
                            name={`extraInfo[${index}].value`}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                          <IconButton size="small" onClick={() => remove(index)}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </div>
                      ))}
                    <div className={classes.extraAttr}>
                      <DefaultButton
                        style={{ flex: 1, marginTop: 10 }}
                        className={classes.btnFullWidth}
                        stylization="secondary"
                        onClick={() => push({ name: "", value: "" })}
                      >
                        {`+ "Incluir informação"`}
                      </DefaultButton>
                    </div>
                  </>
                )}
              </FieldArray>
              */}
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  )
}

export default ContactModal
